"use client";

import {
  CircleAlert,
  CircleCheck,
  Lightbulb,
  TriangleAlert,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  isValidElement,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { useOptionalI18n } from "@/lib/i18n";

export type AlertTone = "info" | "success" | "warning" | "error";

type AlertProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  tone?: AlertTone;
  title?: ReactNode;
  /** A short summary that runs on after the title, on the same line. */
  description?: ReactNode;
  children?: ReactNode;
  /** A text button, drawn in the tone colour after the message. */
  action?: ReactNode;
  icon?: LucideIcon;
  /** Without it the alert hides itself until its message changes. */
  onDismiss?: () => void;
};

// Non-color cue per tone so the meaning survives without color.
const alertToneIcons: Record<AlertTone, LucideIcon> = {
  info: Lightbulb,
  success: CircleCheck,
  warning: TriangleAlert,
  error: CircleAlert,
};

// The words an alert shows, so a hidden alert returns when they change,
// such as a new error, and not on every render that rebuilds its elements.
function textOf(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return `${node}`;
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (isValidElement<{ children?: ReactNode }>(node))
    return textOf(node.props.children);
  return "";
}

/** Single presentation for error, warning, success, and info messages. */
export function Alert({
  tone = "info",
  title,
  description,
  children,
  action,
  icon,
  onDismiss,
  className,
  role,
  ...props
}: AlertProps) {
  const [hidden, setHidden] = useState<string | null>(null);
  const content = [title, description, children].map(textOf).join("\u0000");
  if (!onDismiss && hidden === content) return null;
  const ToneIcon = icon ?? alertToneIcons[tone];
  // A lone sentence reads as the body whichever prop carried it, so a
  // message-only alert never takes the weight of a title.
  const heading = children != null || description != null ? title : null;
  const message = children ?? (description == null ? title : null);
  return (
    <div
      {...props}
      // An error alert must always announce assertively.
      role={tone === "error" ? "alert" : (role ?? "status")}
      // Full class names spelled out so the CSS consistency check sees them.
      className={[
        "alert",
        tone === "info" && "alert-info",
        tone === "success" && "alert-success",
        tone === "warning" && "alert-warning",
        tone === "error" && "alert-error",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <ToneIcon className="alert-icon" aria-hidden="true" />
      <div className="alert-body">
        {(heading != null || description != null) && (
          <div className="alert-headline">
            {heading != null && <span className="alert-title">{heading}</span>}
            {description != null && (
              <span className="alert-summary">{description}</span>
            )}
          </div>
        )}
        {message != null && <div className="alert-description">{message}</div>}
      </div>
      {action != null && <div className="alert-action">{action}</div>}
      <AlertDismiss onDismiss={onDismiss ?? (() => setHidden(content))} />
    </div>
  );
}

// Outside a LocalizationProvider, which only tests render in, there is no
// label to give the close button, so it is left out.
function AlertDismiss({ onDismiss }: { onDismiss: () => void }) {
  const i18n = useOptionalI18n();
  if (!i18n) return null;
  return (
    <button
      type="button"
      className="alert-dismiss"
      aria-label={i18n.t("common.dismiss")}
      onClick={onDismiss}
    >
      <X aria-hidden="true" />
    </button>
  );
}
